import type { GameView, PlayerCommand } from "@coup/shared";
import { useState } from "react";
import { Avatar } from "../../components/Avatar";
import { CardFace } from "../../components/Card";
import ui from "../../components/ui.module.css";
import { roleName, type Names } from "./names";
import styles from "./Table.module.css";

interface Props {
  view: GameView;
  names: Names;
  onCommand: (cmd: PlayerCommand) => void;
}

/** Why the local player is losing a card, from the current log entry. */
function lossReason(view: GameView, n: Names): string {
  const entry = view.log.findLast((e) => e.t === "action");
  const last = entry?.t === "action" ? entry.rows.at(-1) : undefined;
  if (last?.t === "reveal") return `${n.name(last.by)} revealed ${roleName(last.role)}`;
  if (last?.t === "concede") return "You conceded the challenge";
  const a = view.action;
  if (a?.kind === "coup") return `${n.name(a.actor)} launched a Coup on you`;
  if (a?.kind === "assassinate") return `${n.name(a.actor)} assassinates you`;
  return "You lose a card";
}

export function LoseCardModal({ view, names, onCommand }: Props) {
  return (
    <div className={styles.modalWrap}>
      <div className={ui.modal} style={{ width: 460 }}>
        <div className={styles.modalContext}>{lossReason(view, names)}</div>
        <div className={styles.modalTitle}>Choose a card to lose</div>
        <div style={{ display: "flex", gap: 24 }}>
          {view.hand.map((r, i) => (
            <button
              key={i}
              type="button"
              className={styles.cardPick}
              onClick={() => onCommand({ t: "loseCard", index: i })}
            >
              <CardFace role={r} size="lg" />
              <span className={styles.loseTag}>Lose {roleName(r)}</span>
            </button>
          ))}
        </div>
        <div className={styles.modalNote}>
          The card goes face up into Exiled for everyone to see.
        </div>
      </div>
    </div>
  );
}

export function ExchangeModal({ view, onCommand }: Props) {
  const [keep, setKeep] = useState<number[]>([]);
  if (view.options.t !== "exchange") return null;
  const { cards, keep: n } = view.options;
  const toggle = (i: number) =>
    setKeep((k) => (k.includes(i) ? k.filter((x) => x !== i) : k.length < n ? [...k, i] : k));

  return (
    <div className={styles.modalWrap}>
      <div className={ui.modal} style={{ width: 640 }}>
        <div className={styles.modalContext}>Exchange — only you can see these cards</div>
        <div className={styles.modalTitle}>
          Keep {n} of {cards.length}
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          {cards.map((r, i) => {
            const on = keep.includes(i);
            const tag = i < view.hand.length ? "Your card" : "Drawn";
            return (
              <button
                key={i}
                type="button"
                className={styles.cardPick}
                aria-pressed={on}
                aria-label={`${on ? "Unselect" : "Keep"} ${roleName(r)}`}
                style={{ opacity: on ? 1 : 0.6 }}
                onClick={() => toggle(i)}
              >
                <div
                  className={styles.exchangeRing}
                  style={{
                    outline: on ? "3px solid var(--timer)" : "1px solid var(--line-strong)",
                  }}
                >
                  <CardFace role={r} size="md" />
                </div>
                <span className={styles.modalNote}>{on ? `${tag} · keeping` : tag}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className={ui.primary}
          style={{ height: 48, padding: "0 28px" }}
          disabled={keep.length !== n}
          onClick={() => onCommand({ t: "keepCards", indices: keep })}
        >
          Keep selected · {keep.length}/{n}
        </button>
      </div>
    </div>
  );
}

interface WinProps {
  view: GameView;
  names: Names;
  isHost: boolean;
  onPlayAgain: () => void;
  onBack: () => void;
}

export function WinModal({ view, names, isHost, onPlayAgain, onBack }: WinProps) {
  if (view.phase.t !== "over") return null;
  const { winner, winnerHand } = view.phase;
  return (
    <div className={styles.modalWrap}>
      <div className={ui.modal} style={{ width: 480, padding: 32, gap: 14 }}>
        <div className={styles.winKicker}>The court has fallen</div>
        <Avatar
          avatar={names.avatar(winner)}
          size={104}
          ring={5}
          style={{ boxShadow: "0 0 0 3px var(--walnut), 0 0 40px rgba(242,194,48,0.45)" }}
        />
        <div className={styles.winName}>
          {winner === view.you ? "You win" : `${names.name(winner)} wins`}
        </div>
        <div className={styles.modalNote} style={{ fontSize: 14 }}>
          Held to the end:
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          {winnerHand.map((r, i) => (
            <CardFace key={i} role={r} size="sm" />
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 8, width: "100%" }}>
          {isHost && (
            <button
              type="button"
              className={ui.primary}
              style={{ flex: 1, height: 50 }}
              onClick={onPlayAgain}
            >
              Play again
            </button>
          )}
          <button
            type="button"
            className={ui.ghost}
            style={{ flex: 1, height: 50, fontSize: 16 }}
            onClick={onBack}
          >
            Back to lobby
          </button>
        </div>
      </div>
    </div>
  );
}
